import type { RouteLocationNormalized } from 'vue-router';
import { defu } from 'defu';

/**
 * Used to load dynamic content for the offer flow from Contentful CMS
 */
export default function useContent(route?: RouteLocationNormalized) {
    const { product } = useProduct(route);
    const { locale } = useNuxtApp().$i18n;
    const { t: $t } = useNuxtApp().$i18n;

    /**
     * Fetch content from CMS based on contentfulId defined in (dynamic) product config
     */
    function fetchCMSContent(id: string) {
        return $fetch<OfferFlowContent>('/api/cms/offerFlowContent', {
            query: {
                locale: locale.value,
                id,
            },
        });
    }

    /**
     * Load Offer Flow content from CMS, based on contentfulId defined in product config.
     *
     * The content is only loaded if the locale of the content is different from the current (already loaded)
     * locale, or if there is no content yet. This prevents reloading the content on every page navigation.
     *
     */
    async function applyContent() {
        // If content is already loaded for the current locale, we dont need to load it again
        if (product.value.status.contentLocale === locale.value) {
            return;
        }

        // Load and merge base content with CMS content
        try {
            if (product.value.config.contentfulId) {
                const content = await fetchCMSContent(product.value.config.contentfulId);
                product.value.content = defu(content ?? {}, product.value.content);
                product.value.status.contentLocale = locale.value;
            }
        }
        catch (error) {
            throw createError({
                message: $t('offerFlow.productConfigPresets.validation.invalid'),
                fatal: true,
                cause: error,
            });
        }
    }

    return {
        applyContent,
    };
}
